import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "src/core/lib/utils";

type DialogVariant = "default" | "entity" | "right-panel";

const DialogVariantContext = React.createContext<DialogVariant>("default");

const dialogContentVariants = cva("", {
  variants: {
    variant: {
      default:
        "py-10 px-20 rounded-lg fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] bg-dialog border border-dialog-border duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
      entity:
        "rounded-lg fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] bg-dialog border border-dialog-border outline-none focus:outline-none focus-visible:outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
      "right-panel":
        "fixed right-0 top-0 z-50 bg-dialog border-l border-dialog-border rounded-l-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const dialogHeaderVariants = cva("", {
  variants: {
    variant: {
      default:
        "flex flex-col h-30 text-left text-lg font-medium mb-20 leading-[30px] justify-center",
      entity: "flex flex-col text-left text-lg font-medium mb-10 leading-[30px] justify-center",
      "right-panel":
        "flex flex-col h-30 text-left text-lg font-medium mb-20 leading-[30px] justify-center",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const dialogFooterVariants = cva("", {
  variants: {
    variant: {
      default: "flex flex-row-reverse h-40 mt-30",
      entity: "flex justify-center h-50 py-5 border-t border-border",
      "right-panel": "flex flex-row-reverse h-40 mt-30",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const dialogTitleVariants = cva("", {
  variants: {
    variant: {
      default: "text-lg font-medium",
      entity: "text-lg font-medium",
      "right-panel": "text-lg font-medium",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const dialogDescriptionVariants = cva("", {
  variants: {
    variant: {
      default: "font-light",
      entity: "font-light",
      "right-panel": "font-light",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type DialogProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  variant?: DialogVariant;
};

const Dialog = ({ variant = "default", ...props }: DialogProps) => (
  <DialogVariantContext.Provider value={variant}>
    <DialogPrimitive.Root {...props} />
  </DialogVariantContext.Provider>
);

const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = {
  hideCloseButton?: boolean;
} & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, ...props }, ref) => {
  const variant = React.useContext(DialogVariantContext);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ variant }), className)}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = React.useContext(DialogVariantContext);
  return <div className={cn(dialogHeaderVariants({ variant }), className)} {...props} />;
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = React.useContext(DialogVariantContext);
  return <div className={cn(dialogFooterVariants({ variant }), className)} {...props} />;
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(DialogVariantContext);
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(dialogTitleVariants({ variant }), className)}
      {...props}
    />
  );
});
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(DialogVariantContext);
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(dialogDescriptionVariants({ variant }), className)}
      {...props}
    />
  );
});
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
